import { Amplify, Auth } from 'aws-amplify';
import { isNull } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useBoolean } from 'react-use';
import { Outlet } from 'react-router-dom';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import * as Sentry from '@sentry/react';
import { CognitoUserContext, CognitoUserCtx } from 'domains/auth/AWSCognitoWrapper/context';
import config from 'config';
import { Loading } from 'components/AppLoader/Loading';
import { CognitoUser } from 'domains/auth/AWSCognitoWrapper/types';

Amplify.configure({
  aws_project_region: config.get('awsProjectRegion'),
  aws_cognito_region: config.get('awsCognitoRegion'),
  aws_user_pools_id: config.get('awsUserPoolsId'),
  aws_user_pools_web_client_id: config.get('awsUserPoolsWebClientId'),
  oauth: {},
});

/**
 * This component acts as a container for user session, will automatically read if one exists
 */
export const AWSCognitoWrapper = () => {
  const [isLoadingSession, setIsLoadingSession] = useBoolean(true);

  const [user, setUser] = useState<CognitoUser | null>(null);

  const ldClient = useLDClient();

  const onSuccessfulLogin = useCallback(
    (u: CognitoUser) => {
      Sentry.setTag('isLoggedIn', true);
      Sentry.setUser({
        email: u.attributes.email,
        id: u.username,
      });
      setUser(u);

      // LaunchDarkly is initialized without user information and gets
      // flags for the anonymous user. This is the moment where it
      // refetches the flags with the correct user ID.
      ldClient?.identify({ key: u.attributes.email });
    },
    [ldClient],
  );

  const readSavedSession = useCallback(() => {
    Auth.currentAuthenticatedUser()
      .then(u => {
        onSuccessfulLogin(u);
      })
      .catch(() => {
        setUser(null);
      })
      .finally(() => setIsLoadingSession(false));
  }, []);

  useEffect(() => {
    readSavedSession();
  }, []);

  const logout = useCallback(() => {
    setUser(null);

    return Auth.signOut();
  }, []);

  const onRefreshSession = useCallback(() => {
    setIsLoadingSession(true);
    readSavedSession();
  }, []);

  if (isLoadingSession) {
    return <Loading className="transition-all duration-100 z-10" />;
  }

  const contextValue: CognitoUserContext = isNull(user)
    ? {
        isLoggedIn: false,
        onLogin: onSuccessfulLogin,
        onRefreshSession,
      }
    : {
        isLoggedIn: true,
        signOut: logout,
        user,
        onRefreshSession,
      };

  return (
    <CognitoUserCtx.Provider value={contextValue}>
      <Outlet />
    </CognitoUserCtx.Provider>
  );
};
